import { apiFetch } from "../../api/client";
import type {
  ProviderCalendarDto,
  ProviderMediaAssetDto,
  ProviderOwnerDashboardSnapshotDto,
  ProviderOwnerMutationSuccessDto,
  ProviderOwnerStatusDto,
  ProviderPricingItemDto,
  ProviderTeamMemberDto,
  SaveProviderCalendarStepDto,
  SaveProviderDocumentsStepDto,
  SaveProviderMediaStepDto,
  SubmitProviderOnboardingForReviewResponseDto,
  UpsertProviderMediaAssetDto,
  UpsertProviderPricingItemDto,
  UpsertProviderTeamMemberDto,
} from "./provider-owner.types";

type ProviderId = string;

const providersBase = "/provider-owner/providers" as const;
const providerPath = (providerId: ProviderId) => `${providersBase}/${providerId}`;
const providerOnboardingPath = (providerId: ProviderId) =>
  `/provider/onboarding/${providerId}`;

export const providerOwnerApi = {
  getDashboardSnapshot: async (
    providerId: ProviderId,
  ): Promise<ProviderOwnerDashboardSnapshotDto> => {
    return apiFetch<ProviderOwnerDashboardSnapshotDto>(
      `${providerPath(providerId)}/dashboard`,
    );
  },

  getOnboardingStatus: async (
    providerId: ProviderId,
  ): Promise<ProviderOwnerStatusDto> => {
    return apiFetch<ProviderOwnerStatusDto>(
      `${providerPath(providerId)}/onboarding-status`,
    );
  },

  submitOnboardingForReview: async (
    providerId: ProviderId,
  ): Promise<SubmitProviderOnboardingForReviewResponseDto> => {
    return apiFetch<SubmitProviderOnboardingForReviewResponseDto>(
      `${providerPath(providerId)}/onboarding-status/submit`,
      {
        method: "POST",
        body: JSON.stringify({}),
      },
    );
  },

  getMediaAssets: async (providerId: ProviderId): Promise<ProviderMediaAssetDto[]> => {
    return apiFetch<ProviderMediaAssetDto[]>(`${providerPath(providerId)}/media`);
  },

  createMediaAsset: async (
    providerId: ProviderId,
    payload: UpsertProviderMediaAssetDto,
  ): Promise<ProviderMediaAssetDto> => {
    return apiFetch<ProviderMediaAssetDto>(`${providerPath(providerId)}/media`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  updateMediaAsset: async (
    providerId: ProviderId,
    mediaId: string,
    payload: UpsertProviderMediaAssetDto,
  ): Promise<ProviderMediaAssetDto> => {
    return apiFetch<ProviderMediaAssetDto>(
      `${providerPath(providerId)}/media/${mediaId}`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );
  },

  deleteMediaAsset: async (
    providerId: ProviderId,
    mediaId: string,
  ): Promise<ProviderOwnerMutationSuccessDto> => {
    return apiFetch<ProviderOwnerMutationSuccessDto>(
      `${providerPath(providerId)}/media/${mediaId}`,
      {
        method: "DELETE",
      },
    );
  },

  getPricingItems: async (providerId: ProviderId): Promise<ProviderPricingItemDto[]> => {
    return apiFetch<ProviderPricingItemDto[]>(`${providerPath(providerId)}/pricing`);
  },

  createPricingItem: async (
    providerId: ProviderId,
    payload: UpsertProviderPricingItemDto,
  ): Promise<ProviderPricingItemDto> => {
    return apiFetch<ProviderPricingItemDto>(`${providerPath(providerId)}/pricing`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  updatePricingItem: async (
    providerId: ProviderId,
    pricingId: string,
    payload: UpsertProviderPricingItemDto,
  ): Promise<ProviderPricingItemDto> => {
    return apiFetch<ProviderPricingItemDto>(
      `${providerPath(providerId)}/pricing/${pricingId}`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );
  },

  deletePricingItem: async (
    providerId: ProviderId,
    pricingId: string,
  ): Promise<ProviderOwnerMutationSuccessDto> => {
    return apiFetch<ProviderOwnerMutationSuccessDto>(
      `${providerPath(providerId)}/pricing/${pricingId}`,
      {
        method: "DELETE",
      },
    );
  },

  getTeamMembers: async (providerId: ProviderId): Promise<ProviderTeamMemberDto[]> => {
    return apiFetch<ProviderTeamMemberDto[]>(`${providerPath(providerId)}/team`);
  },

  createTeamMember: async (
    providerId: ProviderId,
    payload: UpsertProviderTeamMemberDto,
  ): Promise<ProviderTeamMemberDto> => {
    return apiFetch<ProviderTeamMemberDto>(`${providerPath(providerId)}/team`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  updateTeamMember: async (
    providerId: ProviderId,
    teamMemberId: string,
    payload: UpsertProviderTeamMemberDto,
  ): Promise<ProviderTeamMemberDto> => {
    return apiFetch<ProviderTeamMemberDto>(
      `${providerPath(providerId)}/team/${teamMemberId}`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );
  },

  deleteTeamMember: async (
    providerId: ProviderId,
    teamMemberId: string,
  ): Promise<ProviderOwnerMutationSuccessDto> => {
    return apiFetch<ProviderOwnerMutationSuccessDto>(
      `${providerPath(providerId)}/team/${teamMemberId}`,
      {
        method: "DELETE",
      },
    );
  },

  saveDocumentsStep: async (
    providerId: ProviderId,
    payload: SaveProviderDocumentsStepDto,
  ): Promise<ProviderOwnerMutationSuccessDto> => {
    return apiFetch<ProviderOwnerMutationSuccessDto>(
      `${providerPath(providerId)}/onboarding/documents`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );
  },

  saveMediaStep: async (
    providerId: ProviderId,
    payload: SaveProviderMediaStepDto,
  ): Promise<ProviderOwnerMutationSuccessDto> => {
    return apiFetch<ProviderOwnerMutationSuccessDto>(
      `${providerPath(providerId)}/onboarding/media`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );
  },

  savePricingStep: async (
    providerId: ProviderId,
    payload: UpsertProviderPricingItemDto,
  ): Promise<ProviderPricingItemDto> => {
    return apiFetch<ProviderPricingItemDto>(
      `${providerPath(providerId)}/onboarding/pricing`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );
  },

  saveTeamStep: async (
    providerId: ProviderId,
    payload: UpsertProviderTeamMemberDto,
  ): Promise<ProviderTeamMemberDto> => {
    return apiFetch<ProviderTeamMemberDto>(
      `${providerPath(providerId)}/onboarding/team`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );
  },

  getCalendar: async (providerId: ProviderId): Promise<ProviderCalendarDto> => {
    return apiFetch<ProviderCalendarDto>(
      `${providerOnboardingPath(providerId)}/calendar`,
    );
  },

  saveCalendarStep: async (
    providerId: ProviderId,
    payload: SaveProviderCalendarStepDto,
  ): Promise<ProviderCalendarDto> => {
    return apiFetch<ProviderCalendarDto>(
      `${providerOnboardingPath(providerId)}/calendar`,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );
  },
};

export type ProviderOwnerApi = typeof providerOwnerApi;